import { concat } from '@ember/helper';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismIcon } from '@mylighthouse/prism-ember';
import EmberTooltip from '@mylighthouse/prism-ember/components/ember-tooltip';
import dayjs from 'dayjs';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { and, not, or } from 'ember-truth-helpers';
import type { Rate } from 'lumen/models';
import type { PeriodParity, PeriodRate } from 'lumen/models/period';
import type { HotelOrMarket, RateLike } from 'lumen/services/hotel-and-market';
import type { requestParams } from 'lumen/utils/constants/rate-constants';
import {
  checkLosRestriction,
  getLosRestriction,
} from 'lumen/utils/los-restriction';
import {
  mapQueryParamsToAPIParams,
  requestRates,
} from 'lumen/utils/rates-request';
import { timeout } from 'lumen/utils/waiters';

interface HotelTooltipsTooltipRateRoomtypeSignature {
  Args: {
    displayOverModal?: boolean;
    extractDatetime: string | undefined;
    fetchOtherLosRates: boolean | undefined;
    forceShowUpdateTime?: boolean;
    formattedRateOverride?: string;
    hasRate: boolean;
    hotel: HotelOrMarket;
    isFuture?: boolean;
    isParityAnalytics?: boolean;
    isUpdating?: boolean;
    ownHotelRate?: PeriodRate | null;
    requestParams?: requestParams;
    roomNameOverride?: string;
    value?: PeriodRate | PeriodParity;
  };
}

export default class HotelTooltipsTooltipRateRoomtype extends Component<HotelTooltipsTooltipRateRoomtypeSignature> {
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare rates: Services['rates'];
  @service declare store: Services['store'];

  @tracked isLoadingRestrictionRate = false;
  @tracked ownRestrictionRate: Rate = {} as Rate;

  forceShowUpdateTime = this.args.forceShowUpdateTime ?? false;

  getCompetitorRatesWithDifferentLosTask = task(
    { restartable: true },
    async () => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { losRestriction } = this;

      if (losRestriction) {
        try {
          // Prevent calling the API too often when sliding the cursor across the window
          await timeout();

          this.isLoadingRestrictionRate = true;

          const startDate = this.args.value?.period.split(',')[0];
          let ownRestrictionRate = {} as Rate;

          if (!startDate) {
            throw new Error('Start date is not available');
          }

          const rates = this.rates.getCachedLosRestrictionRates(
            startDate,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            losRestriction,
          ) as Rate[];
          let foundRates = false;

          rates.some((rate) => {
            if (
              // @ts-expect-error: 'rate.hotelinfo.id' is possibly 'null'.
              rate.hotelinfo.id.toString() ===
              this.args.value?.hotelinfo.toString()
            ) {
              ownRestrictionRate = rate;
              foundRates = true;
            }
            return foundRates;
          });

          if (!foundRates) {
            const queryParams = { ...this.args.requestParams };
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            queryParams.los = losRestriction;

            const apiParams = mapQueryParamsToAPIParams(
              queryParams,
              startDate,
              startDate,
            );
            const ratesQuery = requestRates(
              'rate',
              // @ts-expect-error: Argument of type 'string | null' is not assignable to parameter of type 'string'.
              this.args.hotel.id,
              apiParams,
              this.store,
            );

            // Pauses the task until the yield is resolved
            const ratesResult = await ratesQuery;
            const ratesArray = [...ratesResult];
            this.rates.setCachedLosRestrictionRates(ratesArray);

            ratesResult.forEach((rate) => {
              const hotelInfoId = rate.hotelinfo?.id;
              if (
                // @ts-expect-error: 'hotelInfoId' is possibly 'null'.
                hotelInfoId.toString() === this.args.value?.hotelinfo.toString()
              ) {
                ownRestrictionRate = rate;
              }
            });
          }

          this.ownRestrictionRate = ownRestrictionRate;
          this.isLoadingRestrictionRate = false;
        } catch (error) {
          this.errorTracking.captureException(error);
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          this.alerting.showErrorAlert();
        }

        // Will ONLY execute on a successful try block (to force execution: use finally block)
        this.isLoadingRestrictionRate = false;
      }
    },
  );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get extractDatetimeFromNow() {
    const hoveredHotelRate = this.args.value;

    if (!hoveredHotelRate) {
      return dayjs.utc(this.args.extractDatetime).fromNow();
    }
    const { ownHotelRate } = this.args;
    // For the own hotel: always show the own extract datetime
    if (ownHotelRate && hoveredHotelRate === ownHotelRate) {
      return dayjs.utc(ownHotelRate.extract_datetime).fromNow();
    }
    const extractDatetimeCompetitor = dayjs.utc(
      hoveredHotelRate.extract_datetime,
    );
    // For the competitors: check if the competitor extract datetime is less than one hour ago and check if the own hotel extract datetime is less than one hour ago (catches the no data edge case)
    // IF SO: return the own hotel extract datetime (same as above)
    const oneHourAgo = dayjs.utc().subtract(1, 'hour');
    if (
      ownHotelRate &&
      extractDatetimeCompetitor.isAfter(oneHourAgo) &&
      dayjs.utc(ownHotelRate.extract_datetime).isAfter(oneHourAgo)
    ) {
      return dayjs.utc(ownHotelRate.extract_datetime).fromNow();
    }
    // IF NOT: return the competitor extract datetime (old behaviour)
    return extractDatetimeCompetitor.fromNow();
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedOwnRestrictionRate() {
    return this.rates.formatRate(this.ownRestrictionRate, this.args.hotel);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedRate() {
    if (this.args.formattedRateOverride) {
      return this.args.formattedRateOverride;
    }
    if (this.args.hasRate || this.losRestriction) {
      return this.rates.formatRate(
        this.args.value as number | RateLike | null,
        this.args.hotel,
      );
    }
    return '';
  }

  get hasRoomtype(): boolean {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return (this.roomName && this.roomName !== '') || this.losRestriction;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get losRestriction() {
    if (this.args.fetchOtherLosRates && checkLosRestriction(this.args.value)) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return getLosRestriction(this.args.value);
    }
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rateTypeString() {
    return this.rates.getRateTypeString(
      this.ownRestrictionRate || this.args.value,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get roomName() {
    return (
      this.args.roomNameOverride ||
      this.ownRestrictionRate?.roomName ||
      this.args.value?.room_name
    );
  }

  get showUpdateTime(): boolean {
    return (
      this.forceShowUpdateTime ||
      Boolean(this.args.isFuture && !this.args.isParityAnalytics)
    );
  }

  <template>
    <EmberTooltip
      @delay={{25}}
      @onShow={{this.getCompetitorRatesWithDifferentLosTask.perform}}
      @side="bottom"
      @tooltipClass={{concat
        "base-tooltip rates-table-tooltip"
        (if @displayOverModal " display-over-modal")
      }}
    >
      <div class="rates-table-tooltip-container">
        <table cellspacing="0">
          <thead>
            <tr>
              {{#if (or @hasRate this.losRestriction)}}
                <th>
                  {{t "rates.rate"}}
                </th>
                {{#if this.hasRoomtype}}
                  <th>
                    {{t "general.room_name"}}
                  </th>
                {{/if}}
                {{#if this.rateTypeString}}
                  <th>
                    {{t "settings.reports.rate_type"}}
                  </th>
                {{/if}}
              {{/if}}
              {{#if this.showUpdateTime}}
                <th>
                  {{t "general.updated"}}
                </th>
              {{/if}}
            </tr>
          </thead>
          <tbody>
            <tr>
              {{#if this.isLoadingRestrictionRate}}
                <td>
                  <div class="formatted-rate-container flex justify-between">
                    {{this.formattedRate}}
                    <p class="loading-state-other-los-rate-table"></p>
                  </div>
                </td>
                <td>
                  <p class="loading-state-other-los-rate-table"></p>
                </td>
                <td>
                  <p class="loading-state-other-los-rate-table"></p>
                </td>
                <td>
                  <p class="loading-state-other-los-rate-table"></p>
                </td>
              {{else}}
                {{#if (or @hasRate this.losRestriction)}}
                  <td>
                    {{this.formattedRate}}
                    {{#if (and this.losRestriction this.ownRestrictionRate)}}
                      ({{this.formattedOwnRestrictionRate}})
                    {{/if}}
                  </td>
                  {{#if this.hasRoomtype}}
                    <td class="rate-tooltip-roomtype truncate">
                      {{this.roomName}}
                    </td>
                  {{/if}}
                  {{#if this.rateTypeString}}
                    <td>
                      {{this.rateTypeString}}
                    </td>
                  {{/if}}
                {{/if}}
                {{#if this.showUpdateTime}}
                  <td
                    class="rate-tooltip-extract-time-cell flex items-center gap-100"
                  >
                    {{this.extractDatetimeFromNow}}
                    {{#if (and @value.is_out_of_sync (not @isUpdating))}}
                      <PrismIcon @name="sync-disabled" @size="extra-small" />
                    {{/if}}
                  </td>
                {{/if}}
              {{/if}}
            </tr>
          </tbody>
        </table>
      </div>
    </EmberTooltip>
  </template>
}
